import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button, Input, Modal, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useApolloClient } from 'react-apollo';
import GET_NEXXEN_PUBLISHER_DEALS from '../../../core/GraphQl/Queries/GET_NEXXEN_PUBLISHER_DEALS';

const { Text } = Typography;

const CampaignRequestorNexxenDealsDialog = ({
  visible,
  setVisible,
  onAddSelections,
  selectedDeals = [],
  campaignType,
}) => {
  // Map campaign type to media format for filtering
  const getMediaFormatForCampaignType = (type) => {
    switch (type) {
      case 8: // Video/CTV
        return 'video';
      case 1: // Display
      case 7: // External
      default:
        return 'display';
    }
  };

  const requiredMediaFormat = getMediaFormatForCampaignType(campaignType);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [deals, setDeals] = useState([]);
  const [loadingDeals, setLoadingDeals] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dealsPage, setDealsPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const client = useApolloClient();

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleAddSelections = () => {
    const selectedItems = deals
      .filter((item) => selectedRowKeys.includes(item.id))
      .map((item) => ({
        id: item.id,  // Internal Nexxen ID (required for API)
        dealId: item.dealId,
        name: item.name,
        cpmBid: item.price || null,
      }));

    onAddSelections(selectedItems);
    setVisible(false);
  };

  const rowSelection = {
    type: 'checkbox',
    selectedRowKeys,
    onChange: onSelectChange,
    preserveSelectedRowKeys: true,
  };

  const fetchAllDeals = useCallback(async () => {
    setLoadingDeals(true);
    setLoadingProgress('Loading publisher deals...');
    let allDeals = [];
    let offset = 0;
    const limit = 100;

    try {
      while (true) {
        const { data } = await client.query({
          query: GET_NEXXEN_PUBLISHER_DEALS,
          variables: { offset, limit },
          fetchPolicy: 'network-only',
        });

        const pageData = data?.nexxenPublisherDeals?.data || [];
        const total = data?.nexxenPublisherDeals?.queryTotal || 0;

        allDeals = [...allDeals, ...pageData];
        setLoadingProgress(`Loaded ${allDeals.length} of ${total} deals...`);

        if (allDeals.length >= total || pageData.length === 0) break;
        offset += limit;
      }

      setDeals(allDeals);
    } catch (error) {
      console.error('Error fetching publisher deals:', error);
      message.error('Failed to load publisher deals');
      setDeals([]);
    } finally {
      setLoadingDeals(false);
      setLoadingProgress('');
    }
  }, [client]);

  useEffect(() => {
    if (visible && deals.length === 0) {
      fetchAllDeals();
    }
  }, [visible, deals.length, fetchAllDeals]);

  useEffect(() => {
    if (selectedDeals && selectedDeals.length > 0 && deals.length > 0) {
      // Map dealId from selectedDeals to the record's id for row selection
      const keys = selectedDeals
        .map((d) => {
          const match = deals.find((deal) => deal.dealId === d.dealId);
          return match ? match.id : null;
        })
        .filter(Boolean);
      setSelectedRowKeys(keys);
    } else if (!selectedDeals || selectedDeals.length === 0) {
      setSelectedRowKeys([]);
    }
  }, [selectedDeals, visible, deals]);

  const filteredDeals = useMemo(() => {
    let filtered = deals;

    // Filter by media format if available
    if (requiredMediaFormat) {
      filtered = filtered.filter((deal) =>
        !deal.mediaFormat || deal.mediaFormat === requiredMediaFormat
      );
    }

    // Then apply search filter
    if (searchTerm) {
      filtered = filtered.filter((item) =>
        item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.dealId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  }, [deals, searchTerm, requiredMediaFormat]);

  const paginatedDeals = useMemo(() => {
    const start = (dealsPage - 1) * pageSize;
    return filteredDeals.slice(start, start + pageSize);
  }, [filteredDeals, dealsPage, pageSize]);

  const dealsColumns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 220,
      ellipsis: true,
    },
    {
      title: 'Deal ID',
      dataIndex: 'dealId',
      key: 'dealId',
      width: 180,
      ellipsis: true,
    },
    {
      title: 'Format',
      dataIndex: 'mediaFormat',
      key: 'mediaFormat',
      width: 90,
      render: (format) => {
        const color = format === 'video' ? 'blue' : format === 'audio' ? 'purple' : 'green';
        return format ? <Tag color={color}>{format}</Tag> : '-';
      },
    },
    {
      title: 'CPM',
      dataIndex: 'price',
      key: 'price',
      width: 80,
      render: (value) => (value ? `$${value.toFixed(2)}` : '-'),
    },
    {
      title: 'Deal Type',
      dataIndex: 'dealType',
      key: 'dealType',
      width: 100,
      render: (value) => value || '-',
    },
    {
      title: 'Status',
      dataIndex: 'workflowStatus',
      key: 'workflowStatus',
      width: 90,
      render: (status) => (
        <Tag color={status === 'approved' ? 'green' : 'default'}>
          {status || 'Unknown'}
        </Tag>
      ),
    },
  ];

  const handleDealsPaginationChange = (page, size) => {
    if (size !== pageSize) {
      setPageSize(size);
      setDealsPage(1);
    } else {
      setDealsPage(page);
    }
  };

  // Reset to page 1 when search term changes
  useEffect(() => {
    setDealsPage(1);
  }, [searchTerm]);

  return (
    <Modal
      title={
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingRight: 20,
          }}
        >
          <span>NEXXEN PUBLISHER DEALS</span>
          <div>
            <Button
              type="primary"
              disabled={selectedRowKeys.length === 0}
              onClick={() => handleAddSelections()}
            >
              Add Selections ({selectedRowKeys.length})
            </Button>
          </div>
        </div>
      }
      visible={visible}
      onCancel={() => setVisible(false)}
      footer={null}
      width={900}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="middle">
        <Space direction="vertical" size={4}>
          <Text type="secondary">
            Select publisher deals to add to this line item. Multiple deals can be selected.
          </Text>
          {requiredMediaFormat && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              Showing only <Tag color={requiredMediaFormat === 'video' ? 'blue' : 'green'} style={{ margin: 0 }}>{requiredMediaFormat}</Tag> deals compatible with your campaign type.
            </Text>
          )}
        </Space>

        <Input
          placeholder="Search deals by name or deal ID..."
          prefix={<SearchOutlined />}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          allowClear
        />

        {loadingDeals ? (
          <Space direction="vertical" style={{ width: '100%', padding: 40 }} align="center">
            <Spin />
            <Text type="secondary">{loadingProgress || 'Loading publisher deals...'}</Text>
          </Space>
        ) : (
          <Table
            rowKey={(record) => record.id}
            rowSelection={rowSelection}
            columns={dealsColumns}
            dataSource={paginatedDeals}
            pagination={{
              current: dealsPage,
              pageSize: pageSize,
              total: filteredDeals.length,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50'],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
              onChange: handleDealsPaginationChange,
            }}
            scroll={{ y: 400 }}
            size="small"
          />
        )}
      </Space>
    </Modal>
  );
};

export default CampaignRequestorNexxenDealsDialog;
